import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Stack,
  Container,
  Button,
  Chip,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,

} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PixIcon from '@mui/icons-material/Pix';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error';
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import format from 'date-fns/format'
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { toast, setMask } from 'utils';
import { UserContext } from 'contexts';
import { useDialog } from 'components/Dialog';
import ImageGallery from 'components/ImageGallery'
import ACL from 'components/ACL';
import Breadcrumbs from 'components/Breadcrumbs';
import Iconify from 'components/Iconify';
import ProfileField from 'components/ProfileField'
import InstallmentCard from 'components/DashCards/ClientInstallmentItem';
import ExternalChargeForm from 'components/DashCards/ExternalChargeForm';
import ExternalChargeRecoveryForm from 'components/DashCards/ExternalChargeRecoveryForm';
import ExternalChargeDetailsTable from 'components/DashCards/ExternalChargeDetailsTable';
import ExternalChargeRefundForm from 'components/DashCards/ExternalChargeRefundForm';
import EditContract from 'sections/dialogs/editContract';

import { contractPayment, chargeInstallments, getContract, getPaymentVoucher, getBillingReceipt, uploadFile, createExternalCharge, createExternalChargeRecovery, cancelExternalCharge } from 'services/requests/contract';
import { deleteFile } from 'services/requests/user'
import EditInstallment from 'sections/dialogs/editInstallment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

dayjs.extend(utc);

export default function ContractDetail() {
  const { t } = useTranslation('common');
  const userContext = useContext(UserContext);
  const { state } = userContext;
  const params = useParams()
  const id = params?.id
  const dialog = useDialog();
  const inputFileRef = useRef(null);

  const { pathname } = useLocation();
  const breadcrumbFromProvider = pathname.includes('todos')

  const [data, setData] = useState(null);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingCharge, setLoadingCharge] = useState(false);
  const [loadingProof, setLoadingProof] = useState(false);
  const [loadingExternalCharge, setLoadingExternalCharge] = useState(false);
  const [loadingExternalRecovery, setLoadingExternalRecovery] = useState(false);
  const [loadingExternalRefund, setLoadingExternalRefund] = useState(false);
  const [editContractOpen, setEditContractOpen] = useState(false);
  const [selectedInstallmentsToPay, setSelectedInstallmentsToPay] = useState([]);
  const [editInstallmentOpen, setEditInstallmentOpen] = useState(false);
  const [externalRecoveryDialogOpen, setExternalRecoveryDialogOpen] = useState(false);
  const [externalRefundDialogOpen, setExternalRefundDialogOpen] = useState(false);
  const getData = () => {
    getContract({ id })
      .then((res) => {
        const contractFiles = [], locationFiles = []
        res.files.forEach(item => (item.type === 'personalLocation' ? locationFiles : contractFiles).push(item))
        setData({ ...res, contractFiles, locationFiles })
      })
      .catch((err) => toast(err, 'error'))
      .finally(() => setLoadingData(false));
  }

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getContractLink = () => {
    if (!data?.contract?.signedFile) return '';
    return `${process.env.REACT_APP_S3_URL}/${process.env.REACT_APP_S3_FOLDER_CONTRACTS}/${data.contract.signedFile}`;
  };

  const handleCopyContractLink = () => {
    const link = getContractLink();
    if (!link) return;

    navigator.clipboard.writeText(link);
    toast('Link copiado para a área de transferência!', 'success');
  };

  const handleAddDocument = () => {
    inputFileRef.current.click()
  }

  const handleSelectFileToAdd = file => {
    setLoadingUpload(true)

    const formData = new FormData()
    formData.append('file', file)

    uploadFile({ id, formData })
      .then(() => {
        toast('Envio realizado com successo!', 'success')
        getData()
      })
      .catch((err) => toast(err, 'error'))
      .finally(() => {
        setLoadingUpload(false)
        inputFileRef.current.value = null
      })
  }

  const handleRemoveFile = key => {
    deleteFile({ key })
      .then(() => getData())
      .catch(err => toast(err, 'error'))
  }

  const showRegistersIds = () => {
    const parseError = (errorString) => {
      if (!errorString) return null
      try {
        const parsed = JSON.parse(errorString)
        return typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : parsed
      } catch {
        return errorString
      }
    }

    dialog({
      title: 'IDs de Registro / QCertifica',
      hideCancel: true,
      confirmationText: 'Ok',
      content: (
        <>
          <Stack direction="column" spacing={2}>
            <Grid container textAlign="left" rowSpacing={1} my={1}>
              <Grid item xs={1} display="flex" alignItems="center">
                <Typography variant="caption" fontWeight="bold">Nº</Typography>
              </Grid>
              <Grid item xs={11} display="flex" alignItems="center">
                <Typography variant="caption" fontWeight="bold">Informações</Typography>
              </Grid>
              {
                data.installments.map((item, key) => {
                  const registerError = parseError(item.qcertificaRegisterError)
                  const conciliationError = parseError(item.qcertificaConciliationError)
                  const hasRegisterError = !!registerError
                  const hasConciliationError = !!conciliationError
                  const hasRegisterId = !!item.qcertificaRegisterId
                  const hasConciliationId = !!item.qcertificaConciliationId

                  return (
                    <React.Fragment key={key}>
                      <Grid item xs={1} display="flex" alignItems="center">
                        <Typography variant="caption">{item.index}</Typography>
                      </Grid>
                      <Grid item xs={11}>
                        <Stack direction="column" spacing={1}>
                          <Grid container>
                            <Grid item xs={1} display="flex" alignItems="center">
                              <Typography variant="caption" fontWeight="bold">R</Typography>
                            </Grid>
                            <Grid item xs={11}>
                              {hasRegisterId ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                                  <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                                    {item.qcertificaRegisterId}
                                  </Typography>
                                </Stack>
                              ) : hasRegisterError ? (
                                <Alert severity="error" sx={{ py: 0.5 }}>
                                  <Typography variant="caption" component="pre" sx={{ 
                                    whiteSpace: 'pre-wrap', 
                                    wordBreak: 'break-word',
                                    fontSize: '0.75rem',
                                    margin: 0
                                  }}>
                                    {registerError}
                                  </Typography>
                                </Alert>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  Pendente
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                          {(hasConciliationId || hasConciliationError) && (
                            <Grid container>
                              <Grid item xs={1} display="flex" alignItems="center">
                                <Typography variant="caption" fontWeight="bold">C</Typography>
                              </Grid>
                              <Grid item xs={11}>
                                {hasConciliationId ? (
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                                      {item.qcertificaConciliationId}
                                    </Typography>
                                  </Stack>
                                ) : hasConciliationError ? (
                                  <Alert severity="error" sx={{ py: 0.5 }}>
                                    <Typography variant="caption" component="pre" sx={{ 
                                      whiteSpace: 'pre-wrap', 
                                      wordBreak: 'break-word',
                                      fontSize: '0.75rem',
                                      margin: 0
                                    }}>
                                      {conciliationError}
                                    </Typography>
                                  </Alert>
                                ) : null}
                              </Grid>
                            </Grid>
                          )}
                        </Stack>
                      </Grid>
                      <Grid xs={12} sx={{ borderBottom: '1px solid #EEE', mt: 0.5 }} />
                    </React.Fragment>
                  )
                })
              }
            </Grid>
            <Box>
              <Typography variant="caption" fontWeight="bold">
                Legenda:
              </Typography>
              <Typography variant="subtitle2">
                R = Registro / C = Conciliação
              </Typography>
            </Box>
          </Stack>
        </>
      ),
    })
      .then(() => { })
      .catch(() => { })
  }

  const payInstallment = (ids) => {
    setLoadingCharge(true)
    chargeInstallments({ body: { installments: ids } })
      .then((res) => {
        setSelectedInstallmentsToPay([])
        dialog({
          title: `Cobrar parcela${ids.length > 1 ? "s" : ""}`,
          hideCancel: true,
          confirmationText: 'Ok',
          content: (
            <>
              <Stack direction="column" spacing={3} alignItems="center">
                <Typography variant="body1">
                  Cobrança via Pix
                </Typography>
                <Typography variant="body2" mt={0}>
                  {setMask('money', res.value)}
                </Typography>

                <QRCodeSVG value={res.openPixBrCode} />

                <Button
                  onClick={() => {
                    window.navigator.clipboard.writeText(res.openPixBrCode)
                    toast('Código PIX copiado com sucesso!', 'success')
                  }}
                >
                  Copiar Código PIX Copia e Cola
                </Button>
              </Stack>
            </>
          ),
        })
          .then(() => { })
          .catch(() => { })
      })
      .catch((err) => toast(err, 'error'))
      .finally(() => setLoadingCharge(false))
  }

  const openSignContract = () => {
    const url = state?.data?.role === 'admin'
      ? `${process.env.REACT_APP_ZAPSIGN_URL}/${data.contract.zapSignSignerLender}`
      : `${process.env.REACT_APP_ZAPSIGN_URL}/${data.contract.zapSignSignerBorrowed}`;

    window.open(url, '_blank');
  }

  const transferValueToClient = () => {
    contractPayment({ id: data.contract.id })
      .then(() => {
        toast('Pagamento criado, acesse o painel OpenPix para aprovar', 'success')
        getData()
      })
      .catch((err) => toast(err, 'error'));
  }

  const viewPaymentVoucher = async () => {
    setLoadingProof(true)
    const payment = await getPaymentVoucher({ id: data.contract.openPixPaymentId })
    setLoadingProof(false)
    if (payment?.payment?.status === 'CONFIRMED' || payment?.payment?.status === 'APPROVED') {
      dialog({
        title: '',
        hideCancel: true,
        confirmationText: 'Ok',
        content: (
          <>
            <Stack direction="column" alignItems="center" textAlign="center">
              <img src="/assets/woovi-logo.webp" className="logo-woovi-comprovante" alt="OpenPix" />

              <CheckCircleIcon color="success" sx={{ fontSize: 70 }} />

              <Typography variant="subtitle1" mt={2}>
                Pagamento Confirmado
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Valor Pago
              </Typography>
              <Typography variant="body1" mt={0}>
                {setMask('money', payment.transaction.value / 100)}
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Detalhes da transação
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Destinatário
              </Typography>
              <Typography variant="body2" mt={0}>
                {payment.destination.name}<br />{payment.destination.pixKey}
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Data e Hora
              </Typography>
              <Typography variant="body2" mt={0}>
                {format(new Date(payment.transaction.time), `dd/MM/yyyy 'às' HH:mm:ss`)}
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                ID da Transação
              </Typography>
              <Typography variant="body2" mt={0}>
                {payment.transaction.endToEndId}
              </Typography>
            </Stack>
          </>
        ),
      })
        .then(() => { })
        .catch(() => { })
    }
  }

  const viewBillingReceipt = async (row) => {
    setLoadingProof(true)
    let payment = null

    try {
      payment = await getBillingReceipt({ id: row.openPixIdentifier })
    } catch (e) {
      if (row.status === 'payed') {
        payment = {
          charge: {
            status: 'COMPLETED',
            paidAt: row.openPixPayedDate,
            identifier: row.openPixIdentifier,
            transactionID: row.openPixIdentifier
          }
        }
      }
    }

    setLoadingProof(false)

    if (payment?.charge?.status === 'COMPLETED') {
      dialog({
        title: '',
        hideCancel: true,
        confirmationText: 'Ok',
        content: (
          <>
            <Stack direction="column" alignItems="center" textAlign="center">
              <img src="/assets/woovi-logo.webp" className="logo-woovi-comprovante" alt="OpenPix" />

              <CheckCircleIcon color="success" sx={{ fontSize: 70 }} />

              <Typography variant="subtitle1" mt={2}>
                Pagamento Confirmado
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Valor Pago
              </Typography>
              <Typography variant="body1" mt={0}>
                {setMask('money', (row.valueToPay))}
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Detalhes da transação
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Destinatário
              </Typography>
              <Typography variant="body2" mt={0}>
                {state.data.provider.name}
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Data e Hora
              </Typography>
              <Typography variant="body2" mt={0}>
                {format(new Date(payment.charge.paidAt), `dd/MM/yyyy 'às' HH:mm:ss`)}
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                Identificador
              </Typography>
              <Typography variant="body2" mt={0}>
                {payment.charge.identifier}
              </Typography>
            </Stack>

            <Stack direction="column" alignItems="center" textAlign="center">
              <Typography variant="subtitle1" mt={3}>
                ID da Transação
              </Typography>
              <Typography variant="body2" mt={0}>
                {payment.charge.transactionID}
              </Typography>
            </Stack>
          </>
        ),
      })
        .then(() => { })
        .catch(() => { })
    }
  }

  const getContractAction = () => {
    if ((state?.data?.role === 'admin' && data.contract.status === 'created') || (state?.data?.role === 'client' && data.contract.status === 'pendingSign')) {
      return <Button
        variant="contained"
        onClick={() => { openSignContract() }}
      >
        Assinar Contrato
      </Button>
    }
    if (state?.data?.role === 'admin' && data.contract.status === 'signed') {
      return <Button
        variant="contained"
        onClick={() => { transferValueToClient() }}
      >
        Liberar crédito
      </Button>
    }
    if (state?.data?.role === 'admin' && data.contract.status === 'pendingPayment' && data.contract.openPixPaymentId) {
      return (
        <>
          <Typography sx={{ mt: 2 }}>
            Pagamento gerado na plataforma OpenPix, aguardando aprovação!<br />
            Busque pelo pagamento com CorrelationId: <b>{data.contract.openPixPaymentId}</b>
          </Typography>
          <a href="https://app.openpix.com/home/payments/list" target="_blank" rel="noreferrer">
            <Button
              variant="contained"
            >
              Acessar painel OpenPix
            </Button>
          </a>
        </>
      )
    }
    if (state?.data?.role === 'admin' && (data.contract.status === 'borrowed' || data.contract.status === 'finished') && process.env.NODE_ENV === 'production' && data.contract?.openPixPaymentId) {
      return <Button
        variant="contained"
        onClick={() => { viewPaymentVoucher() }}
      >
        Comprovante de pagamento
      </Button>
    }
    return null
  }

  const selectInstallment = id => {
    const indexToRemove = selectedInstallmentsToPay.findIndex(item => item === id)
    const arr = [...selectedInstallmentsToPay]
    if (indexToRemove > -1) {
      arr.splice(indexToRemove, 1)
    } else {
      arr.push(id)
    }
    setSelectedInstallmentsToPay([...arr])
  }

  const handleConfirmPayInstallment = ids => {
    if (ids.length > 1) {
      dialog({
        content: (
          <Typography variant="subtitle1">
            Deseja realmente realizar a cobrança de {ids.length} parcelas?
          </Typography >
        )
      })
        .then(() => payInstallment(ids))
        .catch(() => { })

      return
    }

    payInstallment(ids)
  }

  const breadcrumbItems = state?.data?.role === 'assistant' || state?.data?.role === 'legal' ? [
    { label: 'Contrato' }
  ] : [
    { path: `/contratos${breadcrumbFromProvider ? '/todos' : ''}`, label: t(`contracts${breadcrumbFromProvider ? 'Provider' : 'Created'}.title`) },
    { label: 'Detalhe' }
  ]

  const handleEditContract = () => {
    setEditContractOpen(true);
  };

  const handleCloseEditContract = () => {
    setEditContractOpen(false);
  };

  const handleEditInstallment = () => {
    setEditInstallmentOpen(true);
  };

  const handleCloseEditInstallment = () => {
    setEditInstallmentOpen(false);
  };

  const handleCreateExternalCharge = () => {
    const pendingInstallments = data.installments.filter(item => item.status === 'pending');
    const totalDebtValue = pendingInstallments.reduce((sum, item) => {
      return sum + Number(item.valueToPay || item.value);
    }, 0);

    dialog({
      title: 'Cobrança Externa',
      content: (
        <ExternalChargeForm
          onSubmit={(formData) => createExternalChargeRequest(formData)}
          loading={loadingExternalCharge}
          totalDebtValue={totalDebtValue}
          pendingInstallmentsCount={pendingInstallments.length}
        />
      ),
      hideCancel: true,
      hideConfirm: true,
      confirmationText: null,
    });
  };

  const createExternalChargeRequest = (formData) => {
    setLoadingExternalCharge(true);

    createExternalCharge({
      contractId: data.contract.id,
      body: {
        recoveryService: formData.recoveryService,
        recoveryFee: formData.recoveryFee / 100, // Converter porcentagem para decimal
        externalId: formData.externalId || undefined,
        observation: formData.observation || undefined
      }
    })
      .then((res) => {
        toast('Cobrança externa criada com sucesso!', 'success');
        getData();
        dialog({
          title: 'Cobrança Externa Criada',
          hideCancel: true,
          confirmationText: 'Ok',
          content: (
            <Stack direction="column" spacing={2}>
              <Typography variant="body1">
                Cobrança externa enviada com sucesso!
              </Typography>
              <Typography variant="body2">
                <strong>Valor total da dívida:</strong> {setMask('money', res.totalDebt)}
              </Typography>
              <Typography variant="body2">
                <strong>Valor com taxa de recuperação:</strong> {setMask('money', res.totalDebtWithFee)}
              </Typography>
              <Typography variant="body2">
                <strong>Parcelas incluídas:</strong> {res.installments.length}
              </Typography>
              {res.externalCharge.externalId && (
                <Typography variant="body2">
                  <strong>ID Externo:</strong> {res.externalCharge.externalId}
                </Typography>
              )}
            </Stack>
          ),
        });
      })
      .catch((err) => toast(err, 'error'))
      .finally(() => setLoadingExternalCharge(false));
  };

  const handleCreateExternalRecovery = () => {
    setExternalRecoveryDialogOpen(true);
  };

  const createExternalRecoveryRequest = (formData) => {
    setLoadingExternalRecovery(true);

    createExternalChargeRecovery({
      externalChargeId: data.externalCharge.id,
      body: {
        amountReceived: formData.amountReceived,
        recoveryExternalId: formData.recoveryExternalId || undefined,
        observation: formData.observation || undefined
      }
    })
      .then(() => {
        toast('Recuperação registrada com sucesso!', 'success');
        getData();
        setExternalRecoveryDialogOpen(false);
      })
      .catch((err) => toast(err, 'error'))
      .finally(() => setLoadingExternalRecovery(false));
  };

  const handleCancelExternalCharge = () => {
    dialog({
      title: 'Cancelar Cobrança Externa',
      content: (
        <Typography>
          Tem certeza que deseja cancelar a cobrança externa? Esta ação não pode ser desfeita.
        </Typography>
      ),
      confirmationText: 'Cancelar Cobrança'
    })
      .then(() => {
        cancelExternalCharge({ externalChargeId: data.externalCharge.id })
          .then(() => {
            toast('Cobrança externa cancelada com sucesso!', 'success');
            getData();
          })
          .catch((err) => toast(err, 'error'));
      })
      .catch(() => { });
  };

  const handleShowExternalChargeDetails = () => {
    const canRefund = data.externalCharge?.status === 'recovered' && data.externalCharge?.valueToRefund > 0 && !data.externalCharge?.refunded;

    dialog({
      title: 'Detalhes da Cobrança Externa',
      content: (
        <ExternalChargeDetailsTable
          externalCharge={data.externalCharge}
          onRefund={canRefund ? handleCreateExternalRefund : null}
        />
      ),
      hideCancel: true,
      confirmationText: canRefund ? 'Registrar Reembolso' : 'Ok'
    })
      .then(() => {
        if (canRefund) {
          handleCreateExternalRefund();
        }
      })
      .catch(() => { });
  };

  const handleCreateExternalRefund = () => {
    setExternalRefundDialogOpen(true);
  };

  const hasOverdueInstallments = () => {
    return data.installments.filter(item =>
      item.status === 'pending' && new Date(item.maturity) < new Date()
    ).length > 0;
  };

  const computeCreditAllocation = (installments = [], totalAvailable = 0) => {
    let remaining = Number(totalAvailable || 0);
    const allocation = {};

    const pendings = installments
      .filter(inst => inst.status === 'pending')
      .sort((a, b) => new Date(a.maturity) - new Date(b.maturity));

    for (const inst of pendings) {
      const base = Number(inst.valueToPay ?? inst.value ?? 0);
      const applied = Math.max(0, Math.min(remaining, base));
      allocation[inst.id] = applied;
      remaining = Math.max(0, remaining - applied);
      if (remaining === 0) break;
    }
    return allocation;
  };

  const pending = data?.installments?.filter(
  i => i.status === 'pending' || i.status === 'created'
) || [];

const orderedPending = [...pending].sort(
  (a, b) => new Date(a.maturity) - new Date(b.maturity)
);

const totalAvailable = Number(data?.externalCharge?.totalAvailable || 0);

const creditAllocation = totalAvailable > 0
  ? computeCreditAllocation(orderedPending, totalAvailable)
  : {};

const totalPendingWithCredit = orderedPending.reduce((sum, item) => {
  const valor = Number(item.valueToPay ?? item.value ?? 0);
  const credito = Number(creditAllocation[item.id] ?? 0);

  return sum + Math.max(0, valor - credito);
}, 0);

const pendingCount = pending.length;

  return (
    <>
      <Box component="main" mt={1}>
        {
          data && !loadingData ? (
            <Container maxWidth="lg">
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                <Breadcrumbs
                  items={[...breadcrumbItems]}
                />
              </Stack>

              <Grid container rowSpacing={2} columnSpacing={2} mb={3}>
                <Grid xs={12} item sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    disabled={loadingData}
                    onClick={() => getData()}
                    type="link"
                    size="small"
                    endIcon={<Iconify icon="material-symbols:refresh" />}
                  >
                    Atualizar
                  </Button>
                </Grid>


                <Grid item xs={12}>
                  <Card mb={5}>
                    <ACL profiles={['admin', 'seller']}>
                      <CardHeader
                        title="Detalhes do Contrato"
                        subheader={`Contrato nº ${data?.contract?.number || '-'}`}
                        action={
                          <Button
                            variant="text"
                            onClick={handleEditContract}
                            sx={{ mr: 3, alignSelf: 'center' }}
                          >
                            Editar
                          </Button>
                        }
                      />
                    </ACL>
                    <CardContent>
                      <Grid container rowSpacing={2} columnSpacing={2}>
                        <Grid item xs={12} md={6}>
                          <ACL profiles={['admin', 'seller', 'legal']}>
                            <ProfileField
                              label="Cliente"
                              value={data.borrowed.company.name}
                              link={`/clientes/perfil/${data.borrowed.id}`}
                            />
                          </ACL>
                          <ACL profiles={['assistant']}>
                            <ProfileField
                              label="Cliente"
                              value={data.borrowed.name}
                            />
                            <ProfileField
                              value={`(${data.borrowed.company.name})`}
                            />
                          </ACL>
                        </Grid>
                        <ACL profiles={['admin', 'seller', 'legal']}>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="CNPJ do Cliente"
                              value={setMask('99.999.999/9999-99', data.borrowed.company.cnpj)}
                            />
                          </Grid>
                        </ACL>
                        <ACL profiles={['admin', 'legal']}>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Vendedor"
                              value={data.comissioner.company.name}
                              link={`/equipe/perfil/${data.comissioner.id}`}
                            />
                          </Grid>
                        </ACL>
                        <ACL profiles={['admin', 'legal']}>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="CPF do Cliente"
                              value={setMask('999.999.999-99', data.borrowed.cpf)}
                            />
                          </Grid>
                        </ACL>
                        <Grid item xs={12} md={6}>
                          <ProfileField
                            label="Data do Contrato"
                            value={format(new Date(data.contract.created_at), 'dd/MM/yyyy')}
                          />
                        </Grid>
                        <ACL profiles={['admin', 'seller', 'legal']}>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Valor Emprestado"
                              value={setMask('money', data.contract.amount)}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Status"
                              value={t(`contracts.status.${data.contract.status}`)}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Objetivo"
                              value={data.contract.goal}
                            />
                          </Grid>
                        </ACL>
                        <ACL profiles={['admin', 'legal']}>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Dias de carência (atraso parcelas)"
                              value={`${data.contract.installmentDelayDays} dias`}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Juros de mora (atraso parcela)"
                              value={`${data.contract.latePaymentInterest * 100}%`}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Multa de mora (atraso parcela)"
                              value={`${data.contract.latePaymentFine * 100}%`}
                            />
                          </Grid>
                        </ACL>
                      </Grid>

                      <ACL profiles={['admin', 'legal']}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 2,
                            '& > *': {
                              mt: '0 !important'
                            }
                          }}
                        >
                          {data.contract.signedFile && (
                            <Button
                              variant="outlined"
                              onClick={handleCopyContractLink}
                            >
                              <ContentCopyIcon />
                            </Button>
                          )}

                          {data.contract.signedFile && (
                            <Button
                              variant="contained"
                              onClick={() => {
                                const link = getContractLink();
                                if (link) {
                                  window.open(link, '_blank');
                                } else {
                                  toast('Contrato não disponível', 'error');
                                }
                              }}
                            >
                              Contrato
                            </Button>
                          )}

                         {['signed', 'borrowed', 'finished'].includes(data.contract.status) && (
                            <Button
                              variant="contained"
                              onClick={showRegistersIds}
                            >
                              ID's de Registro
                            </Button>
                          )}

                          {getContractAction()}

                          {!data.externalCharge &&
                            data.contract.status === 'borrowed' &&
                            data.installments.some(item => item.status === 'pending') && (
                              <Button
                                variant="contained"
                                color="warning"
                                onClick={handleCreateExternalCharge}
                              >
                                Cobrança Externa
                              </Button>
                            )}
                        </Box>
                      </ACL>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {data.externalCharge && data.externalCharge.status !== 'finished' && data.externalCharge.status !== 'canceled' && (
                <Grid container rowSpacing={2} columnSpacing={2} mb={3}>
                  <Grid item xs={12}>
                    <Card sx={{
                      backgroundColor: data.externalCharge.status === 'finished' ? '#f5f5f5' : '#fafafa',
                      border: data.externalCharge.status === 'finished' ? '1px solid #e0e0e0' : '1px solid #fafafa',
                      overflow: 'hidden'
                    }}>
                      <CardHeader
                        title={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography
                              variant="h6"
                              color={data.externalCharge.status === 'finished' ? "success.dark" : "warning.dark"}
                            >
                              {data.externalCharge.status === 'finished' ? 'Cobrança Externa Finalizada' : 'Cobrança Externa Ativa'}
                            </Typography>
                          </Stack>
                        }
                      />
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Serviço de Recuperação"
                              value={data.externalCharge.recoveryService === 'payhop' ? 'PayHop' : 'Manual'}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Taxa de Recuperação"
                              value={`${(data.externalCharge.recoveryFee * 100).toFixed(2)}%`}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Data de Envio"
                              value={format(new Date(data.externalCharge.sendDatetime), 'dd/MM/yyyy HH:mm')}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Status"
                              value={
                                data.externalCharge.status === 'sent' ? 'Enviado' :
                                  data.externalCharge.status === 'partially_recovered' ? 'Parcialmente Recuperado' :
                                    data.externalCharge.status === 'recovered' ? 'Recuperado' :
                                      data.externalCharge.status === 'finished' ? 'Finalizado' : 'Cancelado'
                              }
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <ProfileField
                              label="Parcelas Recuperadas"
                              value={`${data.externalCharge.recoveredInstallments || 0} de ${data.externalCharge.totalInstallments || 0}`}
                            />
                          </Grid>
                          {data.externalCharge.valueToRefund > 0 && (
                            <Grid item xs={12} md={6}>
                              <ProfileField
                                label="Valor para Reembolso"
                                value={setMask('money', data.externalCharge.valueToRefund)}
                              />
                            </Grid>
                          )}
                          {data.externalCharge.externalId && (
                            <Grid item xs={12} md={6}>
                              <ProfileField
                                label="ID Externo"
                                value={data.externalCharge.externalId}
                              />
                            </Grid>
                          )}
                          {data.externalCharge.observation && (
                            <Grid item xs={12}>
                              <ProfileField
                                label="Observação"
                                value={data.externalCharge.observation}
                              />
                            </Grid>
                          )}
                        </Grid>

                        <ACL profiles={['admin', 'legal']}>
                          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{
                                justifyContent: 'flex-start',
                                flexWrap: 'wrap',
                                gap: 1
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleShowExternalChargeDetails}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                Detalhes
                              </Button>

                              {(data.externalCharge.status === 'sent' || data.externalCharge.status === 'partially_recovered') && (
                                <LoadingButton
                                  variant="outlined"
                                  size="small"
                                  onClick={handleCreateExternalRecovery}
                                  loading={loadingExternalRecovery}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  Receber Valor
                                </LoadingButton>
                              )}

                              {data.externalCharge.status === 'sent' && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={handleCancelExternalCharge}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  Cancelar
                                </Button>
                              )}

                              {(data.externalCharge.status === 'recovered' && data.externalCharge.valueToRefund > 0 && !data.externalCharge.refunded) && (
                                <LoadingButton
                                  variant="contained"
                                  size="small"
                                  color="success"
                                  onClick={handleCreateExternalRefund}
                                  loading={loadingExternalRefund}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  Registrar Reembolso
                                </LoadingButton>
                              )}
                            </Stack>
                          </Box>
                        </ACL>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {
                (data.contract.status !== 'cancelled') ? (
                  <>
                    {
                      data.installments.filter(item => item.status === 'pending' || item.status === 'created').length > 0 ? (
                        <>
                          <Grid item xs={12} mb={3}>
                            {
                              data.contract.status === 'borrowed' ? (
                                <>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={2}
                                    mb={1}
                                  >
                                    <Typography variant="subtitle1">
                                      Parcelas em aberto
                                    </Typography>

                                   {(() => {
                                      const pending = data?.installments?.filter(
                                        i => i.status === 'pending'
                                      ) || [];

                                      const count = pending.length;

                                      if (count === 0) return null;

                                      const totalAvailable = Number(data?.externalCharge?.totalAvailable || 0);

                                      const orderedPending = [...pending].sort(
                                        (a, b) => new Date(a.maturity) - new Date(b.maturity)
                                      );

                                      const creditAllocation = totalAvailable > 0
                                        ? computeCreditAllocation(orderedPending, totalAvailable)
                                        : {};

                                    const total = orderedPending.reduce((sum, item) => {
                                      const valor = Number(item.valueToPay ?? item.value ?? 0);
                                      const credito = Number(creditAllocation[item.id] ?? 0);

                                      const restante = valor - credito;
                                      const restanteFinal = restante - credito;

                                      return sum + Math.max(0, restanteFinal);
                                    }, 0);

                                      return (
                                        <Chip
                                          size="small"
                                          color={hasOverdueInstallments() ? 'error' : 'warning'}
                                          label={`${
                                            count === 1 ? '1 parcela' : `${count} parcelas`
                                          } • ${setMask('money', total)}`}
                                        />
                                      );
                                    })()}
                                  </Stack>

                                  <ACL profiles={['admin', 'seller', 'assistant']}>
                                    <Typography variant="body2" mb={3}>
                                      Selecione as parcelas que deseja efetuar a cobrança
                                    </Typography>
                                  </ACL>
                                  <ACL profiles={['admin', 'seller']}>
                                    {
                                      (data.contract.status === 'borrowed' || data.contract.status === 'created') ? (
                                        <Grid>
                                          <Button
                                            variant="text"
                                            onClick={handleEditInstallment}
                                          >
                                            Editar Vencimentos
                                          </Button>
                                        </Grid>
                                      ) : null
                                    }
                                  </ACL>
                                </>
                              ) : (
                                <>
                                  <Typography variant="subtitle1" mb={3}>
                                    Parcelas Criadas
                                  </Typography>
                                  <ACL profiles={['admin', 'seller']}>
                                    {
                                      data.contract.status === 'created' ? (
                                        <Grid>
                                          <Button
                                            variant="text"
                                            onClick={handleEditInstallment}
                                          >
                                            Editar Vencimentos
                                          </Button>
                                        </Grid>
                                      ) : null
                                    }
                                  </ACL>
                                </>
                              )
                            }
                          </Grid>
                          {
                            selectedInstallmentsToPay?.length > 0 ? (
                              <Grid item xs={12} mb={3}>
                                <LoadingButton
                                  fullWidth
                                  variant="contained"
                                  size="large"
                                  endIcon={<PixIcon />}
                                  onClick={() => handleConfirmPayInstallment(selectedInstallmentsToPay)}
                                  loading={loadingCharge}
                                >
                                  Cobrar {selectedInstallmentsToPay?.length > 1 ? 'parcelas selecionadas' : 'parcela selecionada'}
                                </LoadingButton>
                              </Grid>
                            ) : null
                          }
                          {
                            data.installments
                              .filter(item => item.status === 'pending' || item.status === 'created')
                              .map((item, key) => {
                                const totalAvailable = Number(data.externalCharge?.totalAvailable || 0);
                                const creditAllocation = totalAvailable > 0
                                  ? computeCreditAllocation(data.installments, totalAvailable)
                                  : {};
                                  
                                return (
                                  <Grid item xs={12} md={12} key={key} mb={2}>
                                    <ACL profiles={['admin', 'seller', 'assistant']}>
                                      <InstallmentCard
                                        onClick={data.contract.status === 'borrowed' && item.status === 'pending' ? () => selectInstallment(item.id) : undefined}
                                        highlight={data.contract.status === 'borrowed' && item.status === 'pending' ? selectedInstallmentsToPay.includes(item.id) : undefined}
                                        data={item}
                                        creditApplied={creditAllocation[item.id] || 0}
                                      />
                                    </ACL>
                                    <ACL profiles={['legal']}>
                                      <InstallmentCard
                                        data={item}
                                        creditApplied={creditAllocation[item.id] || 0}
                                      />
                                    </ACL>
                                  </Grid>
                                )
                              })
                          }
                        </>
                      ) : null
                    }
                    {
                      data.installments.filter(item => item.status === 'payed').length > 0 ? (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="subtitle1" mb={3}>
                              Parcelas pagas
                            </Typography>
                          </Grid>
                          {
                            data.installments
                              .filter(item => item.status === 'payed')
                              .map((item, key) => {
                                return (
                                  <Grid item xs={12} md={12} key={key} mb={2}>
                                    <InstallmentCard
                                      onClick={() => (item.status === 'payed' && item?.openPixIdentifier) ? viewBillingReceipt(item) : null}
                                      data={item}
                                      creditApplied={0}
                                    />
                                  </Grid>
                                )
                              })
                          }
                        </>
                      ) : null
                    }
                  </>
                ) : null
              }

              <ACL profiles={['admin', 'seller', 'legal']}>
                <Card sx={{ mt: 4 }}>
                  <CardHeader
                    title={t('myProfile.docs')}
                    action={(
                      <ACL profiles={['admin', 'legal']}>
                        <input
                          ref={inputFileRef}
                          accept="image/png, image/jpeg, image/gif, image/webp, application/pdf"
                          id="document"
                          type="file"
                          hidden
                          onChange={e => handleSelectFileToAdd(e.target.files[0])}
                        />
                        {
                          loadingUpload ? 'Enviando documento...' : null
                        }
                        <LoadingButton
                          color="inherit"
                          onClick={() => handleAddDocument()}
                          loading={loadingUpload}
                        >
                          <AddPhotoAlternate />
                        </LoadingButton>
                      </ACL>
                    )}
                  />
                  <CardContent sx={{ pt: 5 }}>
                    {
                      data.contractFiles?.length > 0 ? (
                        <ImageGallery
                          files={data.contractFiles || []}
                          folder={process.env.REACT_APP_S3_FOLDER_CONTRACTDOCS}
                          onRemoveFile={handleRemoveFile}
                        />
                      ) : (
                        <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
                          <Box
                            component="img"
                            src="/assets/illustrations/illustration_empty_content.svg"
                            sx={{ height: 160, mx: 'auto', mb: 5 }}
                          />
                          <Typography variant="h6" paragraph mb={3}>
                            {t('no_data')}
                          </Typography>
                        </Box>
                      )
                    }
                  </CardContent>
                </Card>
              </ACL>

              <ACL profiles={['assistant']}>
                <Card sx={{ mt: 3 }}>
                  <CardHeader
                    title={t('myProfile.location')}
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <pre>
                      {data.borrowed?.location || t('myProfile.locationEmpty')}
                    </pre>

                    {
                      data.locationFiles?.length > 0 ? (
                        <Box sx={{ mt: 3 }}>
                          <ImageGallery
                            files={data.locationFiles || []}
                            folder={process.env.REACT_APP_S3_FOLDER_USERDOCS}
                            onRemoveFile={handleRemoveFile}
                          />
                        </Box>
                      ) : null
                    }
                  </CardContent>
                </Card>
              </ACL>
            </Container>
          ) : loadingData ? (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={80} sx={{ my: 12 }} />
            </Box>
          ) : null
        }

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loadingProof}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <EditContract
          contractData={data?.contract}
          onSuccess={getData}
          open={editContractOpen}
          onClose={handleCloseEditContract}
        />
        <EditInstallment
          contractData={data?.contract}
          installmentData={data?.installments}
          onSuccess={getData}
          open={editInstallmentOpen}
          onClose={handleCloseEditInstallment}
        />

        {externalRecoveryDialogOpen && (
          <Dialog
            open={externalRecoveryDialogOpen}
            onClose={() => setExternalRecoveryDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Registrar Recuperação Externa</DialogTitle>
            <DialogContent>
              <ExternalChargeRecoveryForm
                externalChargeId={data?.externalCharge?.id}
                onSuccess={() => {
                  getData();
                  setExternalRecoveryDialogOpen(false);
                }}
                onClose={() => setExternalRecoveryDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}

        {externalRefundDialogOpen && (
          <Dialog
            open={externalRefundDialogOpen}
            onClose={() => setExternalRefundDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Registrar Reembolso</DialogTitle>
            <DialogContent>
              <ExternalChargeRefundForm
                externalChargeId={data?.externalCharge?.id}
                onSuccess={() => {
                  getData();
                  setExternalRefundDialogOpen(false);
                }}
                onClose={() => setExternalRefundDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </>
  )
}
